// Session driver: the thin web-side transport to the server-authoritative
// session API. It holds NO orchestrator. Under Option A the orchestrator runs
// server-side; this module only creates a session, fetches the current step,
// submits a step result so the SERVER advances, and pings liveness. The server
// is authoritative for progression and data.
//
// Contract notes (reconciled against the backend in Phase 4 step 4.0):
//   - Create returns a PENDING session (no step yet). The participant number is
//     assigned off the hot path; the caller polls fetchStep until the server
//     has built the orchestrator (see session/runner.ts pollUntilReady).
//   - Auth is the opaque session token in the X-Session-Token header.
//   - submitStep carries the step_index the client believes it is on; a stale
//     or duplicate submit is rejected 409 (replay protection / idempotency).

import { apiRequest, API_BASE, SessionApiError } from "../api/client";
import type {
  StepType,
  StepResult,
  Identity,
  SessionCreateResponse,
  StepResponse,
  SubmitResponse,
  FormResponse,
  SurveyContent,
  SurveyQuestion,
  DocumentContent,
  WritingContent,
  TransitionContent,
  VideoContent,
  TaskContent,
} from "../api/types";

// The capability handle for one session: its id and its opaque token.
export interface SessionHandle {
  sessionId: string;
  sessionToken: string;
  // The study's idle window (seconds) snapshotted for this session; 0 = disabled.
  // The idle watcher reads this; absent/0 => disabled (an older backend, the
  // sandbox preview, or a test handle simply never arms the watcher). Optional so
  // existing 2-field handle literals (tests, preview) stay valid.
  idleTimeoutSeconds?: number;
}

// Open the platform link -> create the (pending) session.
export async function createSession(
  experimentId: string,
  identity: Identity,
): Promise<SessionHandle> {
  const res = await apiRequest<SessionCreateResponse>("/sessions", {
    method: "POST",
    body: { experiment_id: experimentId, identity },
  });
  return {
    sessionId: res.session_id,
    sessionToken: res.session_token,
    idleTimeoutSeconds: res.idle_timeout_seconds ?? 0,
  };
}

// Fetch the current step. This is BOTH the loading-screen poll target (while the
// session is pending) AND the re-sync path after the chat block advances the
// session server-side, and after a reconnect.
export async function fetchStep(h: SessionHandle): Promise<StepResponse> {
  return apiRequest<StepResponse>(`/sessions/${h.sessionId}/step`, {
    sessionToken: h.sessionToken,
  });
}

// The study's Begin-page copy (welcome text + button label). Public (no session
// needed) so the FIRST screen can render before a session exists. Both the real
// participant landing and the owner preview fetch it here, so their first screen
// is identical - the preview shows exactly what the participant will see.
export interface BeginInfo {
  welcome_text: string;
  begin_button_label: string;
}

export async function fetchExperimentBegin(experimentId: string): Promise<BeginInfo> {
  return apiRequest<BeginInfo>(`/experiments/${experimentId}/begin`);
}

// Submit a step result and let the server advance. The step_index guard gives
// replay protection: a stale/duplicate submit is rejected 409 with the
// authoritative index so the client can re-sync.
export async function submitStep(
  h: SessionHandle,
  stepIndex: number,
  type: StepType,
  payload: StepResult,
): Promise<SubmitResponse> {
  return apiRequest<SubmitResponse>(`/sessions/${h.sessionId}/step`, {
    method: "POST",
    sessionToken: h.sessionToken,
    body: { step_index: stepIndex, type, payload },
  });
}

// Liveness ping / idle-expiry signal (browser-side idle detection drives the
// flag; the full idle policy is Phase 8).
export async function heartbeat(h: SessionHandle, idleExpired = false): Promise<void> {
  await apiRequest(`/sessions/${h.sessionId}/heartbeat`, {
    method: "POST",
    sessionToken: h.sessionToken,
    body: { idle_expired: idleExpired },
  });
}

// Tab-close beacon: tell the server the participant left so it abandons this
// session (reason "app_closed") and recycles the slot promptly, instead of
// waiting for the lazy idle sweep. Fire-and-forget on page unload:
//   - keepalive: true so the request survives the unload (sendBeacon can't set
//     the X-Session-Token header, so we can't use it — keepalive fetch can).
//   - NOT routed through apiRequest: its retry/Retry-After logic is unsuitable
//     for unload, and we never want to await or retry here.
export function sendCloseBeacon(h: SessionHandle): void {
  try {
    void fetch(`${API_BASE}/sessions/${h.sessionId}/heartbeat`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", "X-Session-Token": h.sessionToken },
      body: JSON.stringify({ closed: true }),
    }).catch(() => {});
  } catch {
    /* fire-and-forget: never block or throw on unload */
  }
}

// ERASE_HISTORY: ask the server to forget the turns so far in the current chat
// block. The client fires this on its erase timer and then clears its own view;
// stored turns are kept server-side as research data.
export async function eraseChatHistory(h: SessionHandle): Promise<void> {
  await apiRequest(`/sessions/${h.sessionId}/chat/erase`, {
    method: "POST",
    sessionToken: h.sessionToken,
  });
}

// Fetch the current step's form content (consent / debrief). Session-token
// scoped: it only ever returns the form for the step the session is on.
export async function fetchForm(h: SessionHandle): Promise<FormResponse> {
  return apiRequest<FormResponse>(`/sessions/${h.sessionId}/form`, {
    sessionToken: h.sessionToken,
  });
}

export async function fetchSurvey(h: SessionHandle): Promise<SurveyContent> {
  return apiRequest<SurveyContent>(`/sessions/${h.sessionId}/survey`, {
    sessionToken: h.sessionToken,
  });
}

// INTER_TRIAL_SURVEY: the survey named in the current chat block's settings.
// Session-token scoped to the current step, like fetchSurvey.
export interface ChatSurveyContent {
  survey_id: string | null;
  name: string;
  questions: SurveyQuestion[];
}

export interface ChatHistoryMessage {
  role: "user" | "assistant";
  content: string;
}

// The visible conversation to seed the chat screen on block entry. The server
// returns the current display run: prior chat blocks carry over unless a block
// has clear-display on. Also restores the screen after a reload.
export async function fetchChatHistory(
  h: SessionHandle,
): Promise<{ messages: ChatHistoryMessage[] }> {
  return apiRequest<{ messages: ChatHistoryMessage[] }>(
    `/sessions/${h.sessionId}/chat/history`,
    { sessionToken: h.sessionToken },
  );
}

// With no triggerId the server resolves the block's "every N messages" survey
// (the original behaviour). A TIME trigger passes its own id so the server
// resolves that trigger's survey instead.
export async function fetchChatSurvey(
  h: SessionHandle,
  triggerId?: string,
): Promise<ChatSurveyContent> {
  const query = triggerId ? `?trigger_id=${encodeURIComponent(triggerId)}` : "";
  return apiRequest<ChatSurveyContent>(`/sessions/${h.sessionId}/chat/survey${query}`, {
    sessionToken: h.sessionToken,
  });
}

// INTER_TRIAL_SURVEY: log an inline survey's responses against the current chat
// block. Does NOT advance the session; the chat resumes afterward. triggerId
// (time triggers only) tells the server which survey the answers belong to.
export async function submitChatSurvey(
  h: SessionHandle,
  surveyName: string,
  responses: Array<{ question_id: string; response: string }>,
  triggerId?: string,
): Promise<void> {
  await apiRequest(`/sessions/${h.sessionId}/chat/survey`, {
    method: "POST",
    sessionToken: h.sessionToken,
    body: triggerId
      ? { survey_name: surveyName, responses, trigger_id: triggerId }
      : { survey_name: surveyName, responses },
  });
}

export async function fetchDocument(h: SessionHandle): Promise<DocumentContent> {
  return apiRequest<DocumentContent>(`/sessions/${h.sessionId}/document`, {
    sessionToken: h.sessionToken,
  });
}

// Resolve the current writing step's mid-block surveys (with inline questions).
// The prompt/constraints come inline in the /step payload; this only carries the
// surveys the participant can't fetch by id. Session-token scoped to the step.
export async function fetchWriting(h: SessionHandle): Promise<WritingContent> {
  return apiRequest<WritingContent>(`/sessions/${h.sessionId}/writing`, {
    sessionToken: h.sessionToken,
  });
}

// Resolve the current transition step's mid-block surveys (with inline
// questions). The transition's text/settings ride inline in /step.
export async function fetchTransition(h: SessionHandle): Promise<TransitionContent> {
  return apiRequest<TransitionContent>(`/sessions/${h.sessionId}/transition`, {
    sessionToken: h.sessionToken,
  });
}

// Resolve the current video step's playable source (signed GCS URL for an
// uploaded video, or the researcher's direct URL). The browser <video> streams
// it directly, so there is no companion "/file" byte route like documents have.
export async function fetchVideo(h: SessionHandle): Promise<VideoContent> {
  return apiRequest<VideoContent>(`/sessions/${h.sessionId}/video`, {
    sessionToken: h.sessionToken,
  });
}

// Resolve the current task step's definition (inline or a referenced custom
// task). The engine reads it the same way regardless of source.
export async function fetchTask(h: SessionHandle): Promise<TaskContent> {
  return apiRequest<TaskContent>(`/sessions/${h.sessionId}/task`, {
    sessionToken: h.sessionToken,
  });
}

// The document file bytes are binary, so this bypasses apiRequest (which is
// JSON-only) and reads the proxied bytes directly off the session route.
export async function fetchDocumentFile(h: SessionHandle): Promise<ArrayBuffer> {
  const res = await fetch(`${API_BASE}/sessions/${h.sessionId}/document/file`, {
    headers: { "X-Session-Token": h.sessionToken },
  });
  if (!res.ok) throw new Error(`document file fetch failed: ${res.status}`);
  return res.arrayBuffer();
}

// A/B TESTING (Batch F): on an A/B trigger the client asks the server to
// generate two candidate replies (without storing or advancing), shows them
// side by side, then commits the participant's choice.
export interface AbTurnResult {
  test_type?: "content" | "ui_test" | "ui_alt";
  option_a?: string;
  option_b?: string;
  ended?: boolean; // server ended the session (cap reached / wallet empty)
  reason?: string;
}

export async function generateAbTurn(
  h: SessionHandle,
  input: { turnId: string; message: string },
): Promise<AbTurnResult> {
  return apiRequest<AbTurnResult>(`/sessions/${h.sessionId}/chat/ab`, {
    method: "POST",
    sessionToken: h.sessionToken,
    body: { turn_id: input.turnId, message: input.message },
  });
}

// The Smart Question check verdict for one answer (owner-billed, server-side).
export interface SmartCheckResult {
  category?: string;
  confidence?: number;
  followup_shown?: boolean;
  followup_text?: string;
  rationale?: string;
  check_status?: string;
  rule_version?: number | string;
  // The full run record (answer_1, category, ...); echoed back at survey submit
  // so it lands as the sq_ columns. answer_2 is filled in client-side.
  record?: Record<string, unknown>;
  in_progress?: boolean;
}

export async function smartQuestionCheck(
  h: SessionHandle,
  input: {
    turnId: string;
    questionId: string;
    answer: string;
    // Content-blind typing summary; sent only when provenance is enabled. The
    // backend still gates whether it is stored.
    provenance?: Record<string, unknown>;
  },
): Promise<SmartCheckResult> {
  return apiRequest<SmartCheckResult>(
    `/sessions/${h.sessionId}/smart-question-check`,
    {
      method: "POST",
      sessionToken: h.sessionToken,
      body: {
        turn_id: input.turnId,
        question_id: input.questionId,
        answer: input.answer,
        ...(input.provenance ? { provenance: input.provenance } : {}),
      },
    },
  );
}

export interface AbCommitResult {
  block_complete: boolean;
  assistant_count?: number;
  step_index?: number;
  token_budget?: number;
  tokens_used?: number;
}

export async function commitAbChoice(
  h: SessionHandle,
  input: {
    message: string;
    selection: "A" | "B";
    option_a: string;
    option_b: string;
    test_type: string;
    latencyMs: number;
    turnId?: string;
    end?: boolean;
  },
): Promise<AbCommitResult> {
  return apiRequest<AbCommitResult>(`/sessions/${h.sessionId}/chat/ab/commit`, {
    method: "POST",
    sessionToken: h.sessionToken,
    body: {
      message: input.message,
      selection: input.selection,
      option_a: input.option_a,
      option_b: input.option_b,
      test_type: input.test_type,
      latency_ms: input.latencyMs,
      turn_id: input.turnId ?? "",
      end: input.end ?? false,
    },
  });
}


// ---- Voice (session-token authed; the backend bills the experiment owner) ----
async function throwSessionError(res: Response): Promise<never> {
  let code: string | undefined;
  let message = `Request failed (${res.status})`;
  try {
    const j = (await res.json()) as { message?: string; code?: string };
    if (j.message) message = j.message;
    if (j.code) code = j.code;
  } catch {
    // non-JSON error body; keep the generic message
  }
  throw new SessionApiError(res.status, message, code);
}

// Ask the server to read its own last reply aloud. Returns MP3 audio. On a
// voice-unavailable error (no subscription / tier / credits) the thrown
// SessionApiError carries the code so the caller can fall back to text.
export async function requestTts(h: SessionHandle): Promise<Blob> {
  const res = await fetch(`${API_BASE}/sessions/${h.sessionId}/voice/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Session-Token": h.sessionToken },
    body: JSON.stringify({}),
  });
  if (!res.ok) await throwSessionError(res);
  return await res.blob();
}

// Send a recording up for transcription. STT is free but tier-gated; the
// backend refuses anything over its size backstop.
export async function transcribeAudio(h: SessionHandle, audio: Blob): Promise<{ text: string }> {
  const form = new FormData();
  form.append("audio", audio, "recording.webm");
  const res = await fetch(`${API_BASE}/sessions/${h.sessionId}/voice/stt`, {
    method: "POST",
    headers: { "X-Session-Token": h.sessionToken },
    body: form,
  });
  if (!res.ok) await throwSessionError(res);
  return (await res.json()) as { text: string };
}
